import React, { useEffect, useState } from 'react';
import Page from '../../../components/Page';
import useSettings from '../../../hooks/useSettings';
import { Container, Grid, Box, Typography, Paper, Divider } from '@mui/material';
import { fCurrency } from '../../../utils/formatNumber';
import Logo from '../../../components/Logo';
import { keyframes } from '@emotion/react';

const moveLogo = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

export default function BillingSecondView() {
  const pageName = 'Billing';
  const { themeStretch } = useSettings();
  const [selectedData, setSelectedData] = useState(() => {
    return JSON.parse(localStorage.getItem("selectedData")) || {};
  });

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "secondDisplay") {
        setSelectedData(JSON.parse(event.newValue) || {});
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const storedData = JSON.parse(localStorage.getItem("secondDisplay"));
      if (storedData && JSON.stringify(storedData) !== JSON.stringify(selectedData)) {
        setSelectedData(storedData);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedData]);

  const totalAmountBeforeTax = selectedData?.guests?.billing?.reduce((sum, item) => sum + item.amount, 0) || 0;
  const totalGovTax = totalAmountBeforeTax * 0.025;
  const totalAmount = totalAmountBeforeTax+totalGovTax;
  const paidAmount = selectedData?.guests?.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const balanceAmount = totalAmount - paidAmount;

  const hasData = selectedData?.guests;

  return (
    <Page title={pageName}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        {hasData ? (
          <Grid container spacing={3} alignItems="stretch">
            {/* Left Side - Business Info & Guest Summary */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  boxShadow: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '96vh', marginTop: '2vh'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                  <Logo disabledLink sx={{ height: 70 }} isHorizontal={true} />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">{selectedData.guests.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedData.guests.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Employee: {selectedData?.guests?.user?.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1">Total Before Tax: {fCurrency(totalAmountBeforeTax)}</Typography>
                <Typography variant="subtitle1">Gov Tax (2.5%): {fCurrency(totalGovTax)}</Typography>
                <Typography variant="subtitle1" style={{fontSize: '2em', fontWeight: 800}}>Total: {fCurrency(totalAmount)}</Typography>
                <Typography variant="subtitle1">Paid: {fCurrency(paidAmount)}</Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', color: balanceAmount === 0 ? 'green' : 'red' }}
                >
                  Balance: {balanceAmount === 0 ? 'Fully Paid' : fCurrency(balanceAmount)}
                </Typography>
              </Paper>
            </Grid>

            {/* Right Side - Billing Details */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3, height: '95vh', mt: '2vh', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Billing Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                  {selectedData?.guests?.billing?.length > 0 ? (
                    selectedData.guests.billing.map((bill, index) => {
                      const billDate = new Date(bill.dateTime);
                      return (
                        <Box
                          key={index}
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'background.default',
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            gap: 1,
                            alignItems: 'center',
                            mb: 1.5,
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {bill?.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {bill?.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Qty: {bill?.qty} × {fCurrency(bill?.amount / bill?.qty)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {/*{billDate.toLocaleDateString()} at {billDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} */}
                              by {bill?.user?.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                            {fCurrency(bill?.amount)}
                          </Typography>
                          {index !== selectedData.guests.billing.length - 1 && <Divider sx={{ gridColumn: 'span 2', my: 1 }} />}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
                      No billing records available
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

          </Grid>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '80vh',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Logo
              disabledLink
              sx={{
                height: 100,
                animation: `${moveLogo} 10s linear infinite`,
                position: 'absolute'
              }}
              isHorizontal={true}
            />
          </Box>
        )}
      </Container>
    </Page>
  );
}